
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SearchForm from '@/components/SearchForm';
import BookingModal from '@/components/BookingModal';
import PricingBreakdown from '@/components/booking/PricingBreakdown';
import BookingActions from '@/components/booking/BookingActions';
import { useSupabaseAvailability } from '@/hooks/useSupabaseAvailability';
import { useAmplitudeEvents } from '@/utils/amplitudeEvents';
import { getPropertyShortName } from '@/utils/propertyUtils';
import { parseDateFromUrl, calculateNights, formatPrice } from '@/utils/bookingUtils';

interface BookingCardProps {
  price: number;
  checkIn?: Date | undefined;
  checkOut?: Date | undefined;
  onDateChange?: (checkIn: Date | undefined, checkOut: Date | undefined) => void;
  maxGuests?: number;
  airbnbUrl?: string;
  propertyTitle: string;
  icalUrl?: string;
  unavailableDates?: Date[];
  propertyId?: string;
  property?: {
    rating: number;
    guests: number;
    reviews?: any[];
    images?: string[];
    host?: { name: string };
  };
}

const BookingCard = ({ 
  price, 
  checkIn: externalCheckIn, 
  checkOut: externalCheckOut, 
  onDateChange, 
  maxGuests = 6,
  airbnbUrl,
  propertyTitle,
  icalUrl,
  unavailableDates: externalUnavailableDates = [],
  propertyId,
  property
}: BookingCardProps) => {
  const [searchParams] = useSearchParams();
  const [checkIn, setCheckIn] = useState<Date | undefined>(undefined);
  const [checkOut, setCheckOut] = useState<Date | undefined>(undefined);
  const [guests, setGuests] = useState<string>('1');
  const [nights, setNights] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const CLEANING_FEE = 350;
  const { trackRequestReservation } = useAmplitudeEvents();

  // Get availability data from Supabase using the passed propertyId
  const { unavailableDates: supabaseUnavailableDates, isLoading: availabilityLoading } = useSupabaseAvailability(propertyId || '');

  // Use only Supabase dates for availability
  const allUnavailableDates = supabaseUnavailableDates;

  // Initialize from URL parameters on component mount
  useEffect(() => {
    const checkinParam = searchParams.get('checkin');
    const checkoutParam = searchParams.get('checkout');
    const guestsParam = searchParams.get('guests');

    if (checkinParam) {
      const checkinDate = parseDateFromUrl(checkinParam);
      if (checkinDate) {
        setCheckIn(checkinDate);
      }
    }

    if (checkoutParam) {
      const checkoutDate = parseDateFromUrl(checkoutParam);
      if (checkoutDate) {
        setCheckOut(checkoutDate);
      }
    }

    if (guestsParam) {
      setGuests(guestsParam);
    } else {
      setGuests('1');
    }
  }, []);

  // Sync with external props when they change
  useEffect(() => {
    if (externalCheckIn !== undefined && externalCheckIn !== checkIn) {
      setCheckIn(externalCheckIn);
    }
  }, [externalCheckIn]);

  useEffect(() => {
    if (externalCheckOut !== undefined && externalCheckOut !== checkOut) {
      setCheckOut(externalCheckOut);
    }
  }, [externalCheckOut]);

  // Calculate nights when dates change
  useEffect(() => {
    if (checkIn && checkOut) {
      const calculatedNights = calculateNights(checkIn, checkOut);
      setNights(calculatedNights > 0 ? calculatedNights : 0);
    } else {
      setNights(0);
    }
  }, [checkIn, checkOut]);

  const handleDateChange = (checkInDate: Date | undefined, checkOutDate: Date | undefined) => {
    setCheckIn(checkInDate);
    setCheckOut(checkOutDate);
    
    if (checkInDate && checkOutDate) {
      const calculatedNights = calculateNights(checkInDate, checkOutDate);
      setNights(calculatedNights > 0 ? calculatedNights : 0);
    } else {
      setNights(0);
    }

    // Notify parent component
    onDateChange?.(checkInDate, checkOutDate);
  };

  const handleGuestsChange = (newGuests: string) => {
    setGuests(newGuests);
  };

  const handleBookingClick = () => {
    if (!checkIn || !checkOut) {
      return;
    }
    
    // Track Request Reservation event
    const propertyShortName = getPropertyShortName(propertyTitle);
    const totalReviews = property?.reviews ? property.reviews.length : 0;
    const totalPhotos = property?.images ? property.images.length : 0;
    const hostName = property?.host?.name || '';

    trackRequestReservation(
      checkIn,
      checkOut,
      guests,
      propertyTitle,
      propertyShortName,
      property?.rating || 0,
      property?.guests || maxGuests,
      price,
      totalReviews,
      totalPhotos,
      hostName
    );
    
    setIsModalOpen(true);
  };

  const showPricing = checkIn && checkOut && nights > 0;
  const totalPrice = (nights > 0 ? price * nights : 0) + CLEANING_FEE;

  return (
    <>
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 p-6 shadow-lg sticky top-24">
        <div className="mb-6">
          <div className="flex items-baseline space-x-1">
            <span className="text-2xl font-semibold text-gray-900">{formatPrice(price)}</span>
            <span className="text-gray-600">noche</span>
          </div>
          <div className="text-sm text-gray-500 mt-1">
            Costo de limpieza: {formatPrice(CLEANING_FEE)}
          </div>
        </div>
        
        <SearchForm 
          variant="booking" 
          showTitle={false}
          onDateChange={handleDateChange}
          selectedCheckIn={checkIn}
          selectedCheckOut={checkOut}
          maxGuests={maxGuests}
          unavailableDates={allUnavailableDates}
        />
        
        {availabilityLoading && (
          <div className="mt-4 text-sm text-blue-500">
            🔄 Actualizando disponibilidad...
          </div>
        )}
        
        {showPricing && (
          <PricingBreakdown
            nights={nights}
            price={price}
            cleaningFee={CLEANING_FEE}
          />
        )}

        <BookingActions
          checkIn={checkIn}
          checkOut={checkOut}
          guests={guests}
          propertyTitle={propertyTitle}
          airbnbUrl={airbnbUrl}
          property={property}
          price={price}
          maxGuests={maxGuests}
          onBookingClick={handleBookingClick}
        />
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        propertyTitle={propertyTitle}
        checkIn={checkIn}
        checkOut={checkOut}
        nights={nights}
        totalPrice={totalPrice}
        guests={guests}
        property={property}
        maxGuests={maxGuests}
        price={price}
      />
    </>
  );
};

export default BookingCard;
